import React, { lazy, Suspense, useState, useEffect } from "react";
import { Navigate, Routes, Route, useLocation, useNavigate, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import client from "./apolloClient";
import { useQuery, gql } from "@apollo/client";
import ProtectedRoute from "./components/ProtectedRoute";
import AppShell from "./components/navigation/AppShell";
import LifeSuiteSupport from "./components/LifeSuiteSupport";
import NotificationInboxSync from "./components/NotificationInboxSync";
import InboxLegacyRedirect from "./components/InboxLegacyRedirect";

const Signup = lazy(() => import("./pages/Signup"));
const Login = lazy(() => import("./pages/Login"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const Home = lazy(() => import("./pages/Home"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ChurchHome = lazy(() => import("./pages/ChurchHome"));
const CircleDashboard = lazy(() => import("./pages/CircleDashboard"));
const DiscoverPage = lazy(() => import("./pages/DiscoverPage"));
const Profile = lazy(() => import("./pages/Profile"));
const PrayerCheckIns = lazy(() => import("./pages/PrayerCheckIns"));
const PrayerCenterPage = lazy(() => import("./pages/PrayerCenterPage"));
const PrayerLists = lazy(() => import("./pages/PrayerLists"));
const PrayerListDetail = lazy(() => import("./pages/PrayerListDetail"));
const PrayerEvent = lazy(() => import("./pages/PrayerEvent"));
const Friends = lazy(() => import("./pages/Friends"));
const Settings = lazy(() => import("./pages/Settings"));
const Inbox = lazy(() => import("./pages/Inbox"));
const Groups = lazy(() => import("./components/Groups"));
const MarkdownPage = lazy(() => import("./components/MarkdownPage"));
const UserProfileHomeWrapper = lazy(() => import("./pages/UserProfileHomeWrapper"));
const ChurchPublicPage = lazy(() => import("./pages/ChurchPublicPage"));
const ChurchPrayerIntakeForm = lazy(() => import("./pages/ChurchPrayerIntakeForm"));
const Churches = lazy(() => import("./pages/Churches"));
const ChurchOnboardingWizard = lazy(() => import("./pages/ChurchOnboardingWizard"));
const ProfileSetupWizard = lazy(() => import("./pages/ProfileSetupWizard"));
const PlatformPrayerTagsPage = lazy(() => import("./pages/PlatformPrayerTagsPage"));
const FoundingMemberPromosPage = lazy(() => import("./pages/FoundingMemberPromosPage"));
const PlatformAdminDashboardPage = lazy(() => import("./pages/PlatformAdminDashboardPage"));
const DomainInfrastructurePage = lazy(() => import("./pages/DomainInfrastructurePage"));
const SubscriptionGatePage = lazy(() => import("./pages/SubscriptionGatePage"));
const SubscribePage = lazy(() => import("./pages/SubscribePage"));
const ClaimLicensePage = lazy(() => import("./pages/ClaimLicensePage"));
const DesignSystemPreview = lazy(() => import("./pages/DesignSystemPreview"));
const ChurchSiteCandidatePreviewPage = lazy(() => import("./pages/ChurchSiteCandidatePreviewPage"));
const McpConsentPage = lazy(() => import("./pages/McpConsentPage"));

import { FriendsProvider } from "./contexts/FriendsContext";
import { DomainProvider } from "./contexts/DomainContext";
import {
  INVALID_SESSION_EVENT,
  buildLoginRedirectPath,
  clearAuthSession,
  getStoredToken,
  getStoredUser,
  getValidStoredToken,
  isAuthRoutePath,
  isSessionTokenValid,
  persistAuthSession,
} from "./utils/authSession";
import {
  buildProfileSetupPath,
  needsProfileSetup,
  PROFILE_SETUP_PATH,
} from "./utils/profileSetupRouting";
import {
  requiresChurchOnboarding,
  requiresSubscriptionRenewal,
} from "./utils/subscriptionAccess";
import { isChurchSiteHostname } from "./utils/customDomainHost";

// Redirects a route to another path, substituting `:param` placeholders
// from the matched route params (e.g. "/church-admin/:churchId/analytics").
function ParamRedirect({ to }) {
  const params = useParams();
  const resolved = to.replace(/:([a-zA-Z0-9_]+)/g, (match, key) => params[key] ?? match);
  return <Navigate to={resolved} replace />;
}

function RouteLoadingFallback() {
  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{ minHeight: "35vh", display: "grid", placeItems: "center", color: "text.secondary" }}
    >
      Loading page…
    </Box>
  );
}

const GET_USER_PROFILE = gql`
  query {
    userProfile {
      id
      username
      firstName
      email
      role
      roles
      subscriptionLevel
      billingCadence
      subscriptionStatus
      trialEndsAt
      licenseTier
      platformRole
      membershipPlan
      membershipSource
      foundingMemberOffer
      billingAmount
      foundingMemberPromoCode
      foundingMemberRedeemedAt
      avatarUrl
      headerImageUrl
      profileSetupRequired
      profileSetupCompletedAt
      profileSetupDismissedAt
    }
  }
`;

const GET_MY_CHURCHES = gql`
  query {
    myChurches {
      id
    }
  }
`;

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const customChurchHostname = isChurchSiteHostname(
    typeof window !== "undefined" ? window.location.hostname : ""
  );
  const isPublicChurchRoute = customChurchHostname || location.pathname.startsWith("/church/");
  const isMarketingRoute = ["/overview", "/for-churches", "/community"].includes(location.pathname);
  const isPublicSessionRoute = isPublicChurchRoute || isMarketingRoute || location.pathname === "/";
  const isAuthRoute = isAuthRoutePath(location.pathname);
  const initialToken = getStoredToken();
  const initialSessionToken = isSessionTokenValid(initialToken) ? initialToken : null;
  const [sessionToken, setSessionToken] = useState(() => {
    if (!initialToken) return null;
    if (initialSessionToken) return initialSessionToken;

    clearAuthSession();
    return null;
  });
  const [user, setUser] = useState(null);
  const [authResolved, setAuthResolved] = useState(() => !initialSessionToken);
  const [hadStoredSession, setHadStoredSession] = useState(() => Boolean(initialToken));

  useEffect(() => {
    if (!initialToken) {
      clearAuthSession();
    }

    const validToken = getValidStoredToken();
    if (validToken) {
      setSessionToken((current) => (current === validToken ? current : validToken));
      return;
    }

    setSessionToken(null);
  }, []);

  useEffect(() => {
    const handler = () => {
      setUser(getStoredUser());
    };
    window.addEventListener("user-updated", handler);
    return () => window.removeEventListener("user-updated", handler);
  }, []);

  useEffect(() => {
    const handleInvalidSession = async (event) => {
      const nextPath =
        event?.detail?.nextPath || `${location.pathname}${location.search}${location.hash}`;
      setHadStoredSession(true);
      setSessionToken(null);
      setUser(null);
      setAuthResolved(true);

      try {
        await client.clearStore();
      } catch {
        // Ignore cache reset failures during logout flows.
      }

      if (!isPublicSessionRoute && !isAuthRoute) {
        navigate(buildLoginRedirectPath(nextPath), { replace: true });
      }
    };

    window.addEventListener(INVALID_SESSION_EVENT, handleInvalidSession);
    return () => window.removeEventListener(INVALID_SESSION_EVENT, handleInvalidSession);
  }, [
    isAuthRoute,
    isPublicSessionRoute,
    location.hash,
    location.pathname,
    location.search,
    navigate,
  ]);

  useEffect(() => {
    if (!hadStoredSession || sessionToken || isPublicSessionRoute || isAuthRoute) {
      return;
    }

    navigate(buildLoginRedirectPath(`${location.pathname}${location.search}${location.hash}`), {
      replace: true,
    });
  }, [
    hadStoredSession,
    isAuthRoute,
    isPublicSessionRoute,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    sessionToken,
  ]);

  const {
    data: profileData,
    loading: profileLoading,
    error: profileError,
  } = useQuery(GET_USER_PROFILE, {
    skip: !sessionToken,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!sessionToken || profileLoading) return;
    if (profileData?.userProfile) {
      setUser(profileData.userProfile);
      persistAuthSession(profileData.userProfile, sessionToken);
      setHadStoredSession(true);
      setAuthResolved(true);
      window.dispatchEvent(new Event("user-updated"));
      return;
    }
    setAuthResolved(true);
  }, [profileData, profileError, profileLoading, sessionToken]);

  const isChurchAdminSubscription =
    user?.licenseTier === "CHURCH_ADMIN" || user?.platformRole === "GLOBAL_ADMIN";
  const hasExpiredSubscription = requiresSubscriptionRenewal(user);
  const profileSetupPending = needsProfileSetup(user);
  const isProfileSetupRoute = location.pathname.startsWith(PROFILE_SETUP_PATH);
  const isMcpConsentRoute = location.pathname === "/mcp/consent";

  const { data: churchesData, loading: churchesLoading } = useQuery(GET_MY_CHURCHES, {
    skip: !sessionToken || !isChurchAdminSubscription,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (customChurchHostname || isMarketingRoute) return;
    const hasChurch = (churchesData?.myChurches || []).length > 0;
    const isOnboardingRoute = location.pathname.startsWith("/onboarding/church");
    const isProfileOnboardingRoute = location.pathname.startsWith(PROFILE_SETUP_PATH);
    const isPlatformAdminRoute = location.pathname.startsWith("/admin") || isMcpConsentRoute;
    const isAuthRoute = isAuthRoutePath(location.pathname);
    const skippedOnboarding = sessionStorage.getItem("skipChurchOnboarding") === "1";

    if (requiresChurchOnboarding({
      user,
      isChurchAdminSubscription,
      churchesLoading,
      hasChurch,
      isOnboardingRoute,
      isProfileOnboardingRoute,
      isPlatformAdminRoute,
      isAuthRoute,
      skippedOnboarding,
    })) {
      navigate("/onboarding/church", { replace: true });
    }
  }, [
    churchesData,
    churchesLoading,
    customChurchHostname,
    isMarketingRoute,
    isChurchAdminSubscription,
    location.pathname,
    navigate,
    isMcpConsentRoute,
    user?.platformRole,
  ]);

  const handleAuthSuccess = (userObj, token) => {
    persistAuthSession(userObj, token);
    setSessionToken(token);
    setUser(userObj);
    setHadStoredSession(true);
    setAuthResolved(true);
  };

  const handleLogout = async () => {
    clearAuthSession();
    setSessionToken(null);
    setUser(null);
    setAuthResolved(true);
    setHadStoredSession(false);

    try {
      await client.clearStore();
    } catch {
      // Ignore cache reset failures during logout flows.
    }

    if (!isPublicChurchRoute) {
      navigate("/login", { replace: true });
    }
  };

  const handleProfileSetupResolved = (updatedProfile) => {
    const mergedUser = { ...(user || {}), ...(updatedProfile || {}) };
    setUser(mergedUser);
    persistAuthSession(mergedUser, sessionToken);
    window.dispatchEvent(new Event("user-updated"));
  };

  const renderProtectedElement = (
    element,
    { allowExpired = false, requireChurchAdmin = false } = {}
  ) => {
    if (profileSetupPending && !isProfileSetupRoute) {
      return <Navigate to={buildProfileSetupPath(location)} replace />;
    }
    if (hasExpiredSubscription && requireChurchAdmin && !allowExpired) {
      return <SubscriptionGatePage user={user} onLogout={handleLogout} />;
    }

    if (requireChurchAdmin && !isChurchAdminSubscription) {
      return <Navigate to="/churches" replace />;
    }

    return element;
  };

  const routes = (
    <Routes>
      <Route
        path="/"
        element={
          user ? (
            renderProtectedElement(<Home user={user} onLogout={handleLogout} />)
          ) : (
            <LandingPage onAuthSuccess={handleAuthSuccess} />
          )
        }
      />
      <Route
        path="/signup"
        element={<Signup onSuccess={handleAuthSuccess} />}
      />
      <Route path="/overview" element={<LandingPage />} />
      <Route path="/for-churches" element={<LandingPage audience="church" />} />
      <Route path="/community" element={<LandingPage audience="community" />} />
      <Route path="/login" element={<Login onSuccess={handleAuthSuccess} />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/church/:slug" element={<ChurchPublicPage />} />
      <Route path="/church/:slug/pray" element={<ChurchPrayerIntakeForm />} />
      <Route path="/church/:slug/:pageSlug" element={<ChurchPublicPage />} />

      <Route path="/docs/requirements" element={<MarkdownPage srcUrl="/docs/Requirements.md" />} />
      <Route path="/docs/architecture" element={<MarkdownPage srcUrl="/docs/Solution_Architecture.md" />} />
      <Route path="/docs/todo" element={<MarkdownPage srcUrl="/docs/Requirements_and_Checklist.md" />} />
      <Route path="/docs/test-plan" element={<MarkdownPage srcUrl="/docs/Test_Plan.md" />} />

      <Route element={<ProtectedRoute />}>
        <Route
          path="/mcp/consent"
          element={renderProtectedElement(<McpConsentPage />, { allowExpired: true })}
        />
        <Route
          path="/profile"
          element={renderProtectedElement(<Profile user={user} setUser={setUser} />)}
        />
        <Route
          path="/user/:username"
          element={renderProtectedElement(<UserProfileHomeWrapper />)}
        />
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="/lists" element={renderProtectedElement(<PrayerLists user={user} />)} />
        <Route
          path="/lists/:listId"
          element={renderProtectedElement(<PrayerListDetail user={user} />)}
        />
        <Route path="/prayer-check-ins" element={renderProtectedElement(<PrayerCheckIns />)} />
        <Route path="/prayer-center" element={renderProtectedElement(<PrayerCenterPage />)} />
        <Route path="/prayer" element={<Navigate to="/prayer-center" replace />} />
        <Route path="/event" element={<Navigate to="/events" replace />} />
        <Route path="/events" element={renderProtectedElement(<PrayerEvent user={user} />)} />
        <Route path="/discover" element={renderProtectedElement(<DiscoverPage />)} />
        <Route path="/search" element={<Navigate to="/discover" replace />} />
        <Route path="/friends" element={renderProtectedElement(<Friends />)} />
        <Route path="/messages" element={renderProtectedElement(<InboxLegacyRedirect section="messages" />)} />
        <Route path="/notifications" element={renderProtectedElement(<InboxLegacyRedirect section="updates" />)} />
        <Route path="/inbox" element={renderProtectedElement(<Inbox user={user} />)} />
        <Route path="/inbox/updates" element={renderProtectedElement(<Inbox user={user} section="updates" />)} />
        <Route path="/inbox/messages" element={renderProtectedElement(<Inbox user={user} section="messages" />)} />
        <Route path="/settings" element={renderProtectedElement(<Settings />, { allowExpired: true })} />
        <Route path="/subscribe" element={renderProtectedElement(<SubscribePage user={user} />, { allowExpired: true })} />
        <Route path="/claim-license" element={renderProtectedElement(<ClaimLicensePage user={user} />, { allowExpired: true })} />
        <Route path="/circles" element={renderProtectedElement(<Groups user={user} />)} />
        <Route path="/circles/:circleId" element={renderProtectedElement(<ParamRedirect to="/circles/:circleId/feed" />)} />
        <Route path="/circles/:groupId/feed" element={renderProtectedElement(<CircleDashboard />)} />
        <Route path="/groups" element={<Navigate to="/circles" replace />} />
        <Route path="/groups/:groupId" element={<ParamRedirect to="/circles/:groupId" />} />
        <Route path="/churches" element={renderProtectedElement(<Churches />)} />
        <Route path="/churches/:churchId" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/feed" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/groups" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/give" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/members" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/events" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/roles" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/prayer-intake" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/contacts" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/website" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/designer" element={renderProtectedElement(<ChurchHome />)} />
        <Route
          path="/website-builds/:requestId/preview"
          element={renderProtectedElement(<ChurchSiteCandidatePreviewPage />)}
        />
        <Route path="/churches/:churchId/sermons" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/sermons/manage" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/settings" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/audit" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/care/*" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/households/*" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/visitors/*" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/ministries/*" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/volunteers/*" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/communications/*" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/giving/*" element={renderProtectedElement(<ChurchHome />)} />
        <Route path="/churches/:churchId/analytics/*" element={renderProtectedElement(<ChurchHome />)} />
        <Route
          path="/churches/:churchId/insights"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/analytics" />)}
        />
        <Route
          path="/churches/:churchId/admin"
          element={<ParamRedirect to="/churches/:churchId" />}
        />
        <Route
          path="/onboarding/profile"
          element={renderProtectedElement(<ProfileSetupWizard onResolved={handleProfileSetupResolved} />, { allowExpired: true })}
        />
        <Route
          path="/onboarding/church"
          element={renderProtectedElement(<ChurchOnboardingWizard />, { requireChurchAdmin: true })}
        />
        <Route
          path="/church-admin/:churchId/designer"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/designer" />)}
        />
        <Route
          path="/church-admin/:churchId/sermons"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/sermons/manage" />)}
        />
        <Route
          path="/church-admin/:churchId/care"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/care" />)}
        />
        <Route
          path="/church-admin/:churchId/care/queue"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/care/queue" />)}
        />
        <Route
          path="/church-admin/:churchId/care/teams"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/care/teams" />)}
        />
        <Route
          path="/church-admin/:churchId/care/reports"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/care/reports" />)}
        />
        <Route
          path="/church-admin/:churchId/households"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/households" />)}
        />
        <Route
          path="/church-admin/:churchId/households/members"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/households/members" />)}
        />
        <Route
          path="/church-admin/:churchId/households/relationships"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/households/relationships" />)}
        />
        <Route
          path="/church-admin/:churchId/households/care"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/households/care" />)}
        />
        <Route
          path="/church-admin/:churchId/households/attendance"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/households/attendance" />)}
        />
        <Route
          path="/church-admin/:churchId/households/communications"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/households/communications" />)}
        />
        <Route
          path="/church-admin/:churchId/households/:householdId"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/households/:householdId" />)}
        />
        <Route
          path="/church-admin/:churchId/visitors"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/visitors" />)}
        />
        <Route
          path="/church-admin/:churchId/visitors/pipeline"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/visitors/pipeline" />)}
        />
        <Route
          path="/church-admin/:churchId/visitors/membership"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/visitors/membership" />)}
        />
        <Route
          path="/church-admin/:churchId/visitors/reports"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/visitors/reports" />)}
        />
        <Route
          path="/church-admin/:churchId/analytics"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/analytics" />)}
        />
        <Route
          path="/church-admin/:churchId/analytics/church"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/analytics/church" />)}
        />
        <Route
          path="/church-admin/:churchId/analytics/ministry"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/analytics/ministry" />)}
        />
        <Route
          path="/church-admin/:churchId/analytics/visitors"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/analytics/visitors" />)}
        />
        <Route
          path="/church-admin/:churchId/analytics/membership"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/analytics/membership" />)}
        />
        <Route
          path="/church-admin/:churchId/analytics/volunteers"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/analytics/volunteers" />)}
        />
        <Route
          path="/church-admin/:churchId/analytics/care"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/analytics/care" />)}
        />
        <Route
          path="/church-admin/:churchId/analytics/households"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/analytics/households" />)}
        />
        <Route
          path="/church-admin/:churchId/analytics/attendance"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/analytics/attendance" />)}
        />
        <Route
          path="/church-admin/:churchId/visitors/:visitorId"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/visitors/:visitorId" />)}
        />
        <Route
          path="/church-admin/:churchId/ministries"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/ministries" />)}
        />
        <Route
          path="/church-admin/:churchId/ministries/directory"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/ministries/directory" />)}
        />
        <Route
          path="/church-admin/:churchId/ministries/:ministryId"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/ministries/:ministryId" />)}
        />
        <Route
          path="/church-admin/:churchId/volunteers"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/volunteers" />)}
        />
        <Route
          path="/church-admin/:churchId/volunteers/roster"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/volunteers/roster" />)}
        />
        <Route
          path="/church-admin/:churchId/volunteers/:volunteerId"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/volunteers/:volunteerId" />)}
        />
        <Route
          path="/church-admin/:churchId/communications"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/communications" />)}
        />
        <Route
          path="/church-admin/:churchId/communications/announcements"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/communications/announcements" />)}
        />
        <Route
          path="/church-admin/:churchId/communications/campaigns"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/communications/campaigns" />)}
        />
        <Route
          path="/church-admin/:churchId/giving"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/giving" />)}
        />
        <Route
          path="/church-admin/:churchId/giving/funds"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/giving/funds" />)}
        />
        <Route
          path="/church-admin/:churchId/giving/statements"
          element={renderProtectedElement(<ParamRedirect to="/churches/:churchId/giving/statements" />)}
        />
        <Route path="/admin" element={renderProtectedElement(<PlatformAdminDashboardPage />)} />
        <Route
          path="/admin/domain-infrastructure"
          element={renderProtectedElement(<DomainInfrastructurePage />)}
        />
        <Route
          path="/admin/design-system"
          element={user?.platformRole === "GLOBAL_ADMIN"
            ? renderProtectedElement(<DesignSystemPreview />)
            : <Navigate to="/" replace />}
        />
        <Route path="/admin/prayer-tags" element={renderProtectedElement(<PlatformPrayerTagsPage />)} />
        <Route
          path="/admin/founding-member-codes"
          element={renderProtectedElement(<FoundingMemberPromosPage />)}
        />
        <Route path="*" element={renderProtectedElement(<Home user={user} onLogout={handleLogout} />)} />
      </Route>
    </Routes>
  );

  const customDomainRoutes = (
    <Routes>
      <Route path="/" element={<ChurchPublicPage />} />
      <Route path="/pray" element={<ChurchPrayerIntakeForm />} />
      <Route path="/:pageSlug" element={<ChurchPublicPage />} />
      <Route path="*" element={<ChurchPublicPage />} />
    </Routes>
  );

  if (!customChurchHostname && sessionToken && !authResolved) {
    return <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }} />;
  }

  const suspendedRoutes = (
    <Suspense fallback={<RouteLoadingFallback />}>
      {customChurchHostname ? customDomainRoutes : routes}
    </Suspense>
  );

  return (
    <FriendsProvider>
    <DomainProvider>
      <NotificationInboxSync
        active={Boolean(user && sessionToken && !customChurchHostname)}
        onOpenInbox={() => navigate("/inbox")}
      />
      <AppShell
        supportAction={user?.id && sessionToken && !isPublicChurchRoute && !isAuthRoute && !isMcpConsentRoute
          ? <LifeSuiteSupport key={`${user.id}:${sessionToken}`} userId={user.id} sessionToken={sessionToken} /> : null}
        user={user}
        onLogout={handleLogout}
        standalone={(!user && location.pathname === "/") || isMarketingRoute || isPublicChurchRoute || isAuthRoute || isProfileSetupRoute || isMcpConsentRoute}
      >
        {suspendedRoutes}
      </AppShell>
    </DomainProvider>
    </FriendsProvider>
  );
}
